import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAutomationClient } from '../providers/AutomationProvider'
import type { CreateUserParams, UpdateUserParams, UserPreferences } from '../types'

/**
 * Query key factory for users-related queries
 */
export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: () => [...usersKeys.lists()] as const,
  details: () => [...usersKeys.all, 'detail'] as const,
  detail: (id: string) => [...usersKeys.details(), id] as const,
  me: () => [...usersKeys.all, 'me'] as const,
}

/**
 * Hook to fetch all users
 */
export function useUsers() {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: usersKeys.list(),
    queryFn: () => client.users.list(),
  })
}

/**
 * Hook to fetch a single user
 */
export function useUser(id: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: usersKeys.detail(id),
    queryFn: () => client.users.get(id),
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * Hook to fetch current user
 */
export function useCurrentUser() {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: usersKeys.me(),
    queryFn: () => client.users.me(),
  })
}

/**
 * Hook to create a new user
 */
export function useCreateUser() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateUserParams) => client.users.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() })
    },
  })
}

/**
 * Hook to update a user
 */
export function useUpdateUser() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserParams }) =>
      client.users.update(id, data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() })
      queryClient.setQueryData(usersKeys.detail(id), data)
    },
  })
}

/**
 * Hook to update current user's preferences
 */
export function useUpdatePreferences() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UserPreferences) => client.users.updatePreferences(data),
    onSuccess: (data) => {
      queryClient.setQueryData(usersKeys.me(), data)
    },
  })
}
